<template>
    <!--
        To prevent issues that occur from a user not being initialized we use a
        loading layer as a guard to ensure that a user does not progress into the
        application while they are authorizing or are authorized but do not yet
        have scopes loaded and you're not on the user not found page as if so me would be null
        -->
    <hox-loading-layer v-if="isAuthorizing || (isAuthorized && !me && !isUserNotFoundRoute)" size="large" />
    <template v-else>
        <notifications-drawer />
        <component :is="currentTheme" />
        <hox-modal v-if="mediaLibraryModalIsVisible" fullscreen @close="hideMediaLibraryModal">
            <template #header>Content Library</template>
            <media-library />
            <template #footer>
                <portal-target name="media-library-modal-footer" />
            </template>
        </hox-modal>
        <hox-modal v-if="adblockModalIsVisible" @close="hideAdblockModal">
            <template #header>Ad Blocker detected!</template>
            <div>
                You will need to disable your Ad Blocker on *.hoxton.co and *.hogarth.ai to view the banners on this
                platform.
            </div>
            <template #footer>
                <div class="remember-checkbox">
                    <Checkbox :model-value="false" @onChange="rememberAdblockSetting">Don't show this again</Checkbox>
                </div>
                <Button @click="hideAdblockModal">Close</Button>
            </template>
        </hox-modal>
    </template>
    <modal-portal />
</template>

<script lang="ts">
import type { LocationQueryValue } from "vue-router";
import { mapMutations, mapState } from "vuex";

import bus from "@/bus";

import MediaLibrary from "@/components/MediaLibrary.vue";
import HoxModal from "@/components/Modal/Modal/Modal.vue";
import ModalPortal from "@/components/Modal/Modal/ModalPortal.vue";
import NotificationsDrawer from "@/components/Notifications/NotificationsDrawer.vue";

import ThemeCampaignLight from "@/ThemeCampaignLight.vue";
import ThemeDefault from "@/ThemeDefault.vue";
import ThemeLogin from "@/ThemeLogin.vue";
import ThemeTemplatesLight from "@/ThemeTemplatesLight.vue";

import meQuery from "@/apollo/queries/Me.gql";
import rolesQuery from "@/apollo/queries/Roles.gql";
import getContentGenerationApiInfoQuery from "@/apollo/queries/v2/GetContentGenerationApiInfo.gql";
import { AuthMutation } from "@/store/modules/auth";
import { MediaLibraryAction } from "@/store/modules/mediaLibrary";
import { RolesAction } from "@/store/modules/roles";
import { UiAction } from "@/store/modules/ui";

import { getIsAuthorized } from "@/services/Auth";
import { getWppTokenAndContext } from "@/services/wppAuth";

import { GA4Tracker, initGTM, type OpenOSContext } from "ic-analytics";
import { getLandingRoute } from "./router";
import type { Err } from "./types/types";

const AdblockStorageKey = "adblockNoticeDisabled";

export default {
    name: "App",
    components: {
        HoxModal,
        MediaLibrary,
        ModalPortal,
        NotificationsDrawer,
        ThemeCampaignLight,
        ThemeDefault,
        ThemeLogin,
        ThemeTemplatesLight
    },

    data() {
        return {
            isAuthorizing: false,
            localAdblockModalIsVisible: true,
            ga4Tracker: null as GA4Tracker | null,
            ga4Initialized: false,
            openOSContext: null as OpenOSContext | null,
            wppProjectId: null as string | null,
            wppContextReceived: false
        };
    },

    computed: {
        ...mapState("auth", ["isAuthorized", "me", "isWppLogin"]),
        ...mapState("ui", ["theme"]),

        currentTheme() {
            return `theme-${this.theme}`;
        },

        isNetworkErrorRoute() {
            return this.$route.name === "NetworkError";
        },

        isLocalDevNetworkErrorRoute() {
            return this.$route.name === "LocalDevNetworkError";
        },

        isUserNotFoundRoute() {
            return this.$route.name === "UserNotFound";
        },

        getCurrentTheme: {
            get() {
                return this.$store.state.ui.themeMode;
            },

            set(val: string) {
                this.$store.dispatch(UiAction.SetThemeMode, val);
            }
        },

        wrapperClassName() {
            return `app-wrapper wpp-theme wpp-theme-${this.getCurrentTheme.theme || "light"} ${this.$route.name ? `wpp-theme--${(this.$route.name as string).toLowerCase()}` : ""}`;
        },

        adblockModalIsVisible() {
            return (
                this.localAdblockModalIsVisible &&
                !(window as Window & typeof globalThis & { canRunAds: boolean }).canRunAds &&
                !localStorage.getItem(AdblockStorageKey)
            );
        },

        mediaLibraryModalIsVisible() {
            return this.$store.state.mediaLibrary.open;
        },

        isLocalhost() {
            return window.location.hostname.includes("local-dev");
        }
    },

    watch: {
        wrapperClassName() {
            this.updateAppRootClassName();
        },

        me: {
            immediate: true,
            handler(newMe) {
                if (newMe && !this.isWppLogin && !this.ga4Initialized) {
                    this.initializeGA4WithStandaloneUser();
                }
            }
        },

        $route: function (to, from) {
            if (this.ga4Initialized && this.ga4Tracker) {
                const currentUrl = window.location.origin + to.fullPath;
                const previousUrl = from ? window.location.origin + from.fullPath : "";
                const pageTitle = to.meta?.title || document.title;

                this.ga4Tracker.trackVirtualPageView(currentUrl, pageTitle, previousUrl);
            }
        },

        wppProjectId: {
            handler(newProjectId) {
                if (newProjectId && this.ga4Initialized && this.openOSContext) {
                    this.updateGA4WithProjectData();
                }
            }
        },

        openOSContext: {
            deep: true,
            handler(newContext) {
                if (newContext && this.ga4Tracker && !this.ga4Initialized) {
                    this.initializeGA4WithOpenOSContext(newContext);
                }
            }
        }
    },

    async created() {
        let route;

        this.$snackbar.set(this.$Message);
        this.isAuthorizing = true;

        this.initializeGA4();

        if (this.getQueryParam("type") === "wpp") {
            try {
                await getWppTokenAndContext(
                    this.setLocalWppProjectId,
                    this.handleWppContextReceived,
                    this.setLocalWppTenantId,
                    this.setLocalWppWorkspaceId,
                    this.setLocalWppDevhubAppVersionId
                );

                this.setIsWppLogin(true);
                this.setIsAuthorized(true);

                route = getLandingRoute(this.$route);
            } catch (e) {
                console.error("Failed to get WPP token", e);

                try {
                    await this.amplifyAuthorize();
                } catch (err) {
                    this.$snackbar.authentication(err as Err);

                    route = { name: "Login" };
                }
            }
        } else {
            try {
                await this.amplifyAuthorize();
            } catch (err) {
                this.$snackbar.authentication(err as Err);

                route = { name: "Login" };
            }
        }

        const errorDescription = this.getQueryParam("error_description") as string;

        if (errorDescription) {
            if (errorDescription === "User is not assigned to the client application.; error=access_denied") {
                this.$snackbar.info(
                    'Oops! Looks like you don\'t have access.Click <a href="https://hogarthww.sharepoint.com/sites/IntelligentContentHub">here</a> to request access now',
                    "",
                    {
                        closable: true,
                        duration: 0
                    }
                );
            } else {
                this.$snackbar.info("Oops! Looks like you don't have access.", errorDescription, {
                    closable: true,
                    duration: 0
                });
            }
        }

        route = getLandingRoute(this.$route);

        this.isAuthorizing = false;

        if (route) {
            if (route.name !== this.$route.name || this.getQueryParam("code")) {
                this.$router.push(route);
            }
        }

        const savedTheme = localStorage?.getItem("currentTheme");

        if (savedTheme) {
            this.getCurrentTheme = JSON.parse(savedTheme);
        }
    },

    mounted() {
        this.updateAppRootClassName();

        bus.$on("userNotFound", () => {
            if (!this.isUserNotFoundRoute) {
                this.$router.push({ name: "UserNotFound" });
            }
        });

        bus.$on("networkError", () => {
            if (!this.isNetworkErrorRoute) {
                this.$router.push({ name: "NetworkError" });
            }
        });

        bus.$on("localDevNetworkError", () => {
            if (!this.isLocalDevNetworkErrorRoute) {
                this.$router.push({ name: "LocalDevNetworkError" });
            }
        });

        if (this.isAuthorized && this.ga4Initialized) {
            this.trackInitialPageView();
        }
        bus.$on("percentLoadEvent", load => {
            this.$Loading.update(load);

            if (load >= 100) {
                this.$Loading.finish();
            }
        });

        bus.$on("apolloLoadEvent", load => {
            this.$Loading.start();

            if (load === 0) {
                this.$Loading.finish();
            } else {
                this.$Loading.update(100 / load);
            }
        });

        bus.$on("apolloErrorEvent", err => {
            this.$Loading.error();

            // Process global errors
            if (err.statusCode && [401, 403].includes(err.statusCode)) {
                this.$snackbar.unauthorized(err);
            } else if (Array.isArray(err.graphQLErrors)) {
                this.$snackbar.graphQLErrors(err.graphQLErrors);
            }
        });
    },

    methods: {
        ...mapMutations("auth", [
            "setIsAuthorized",
            "setIsWppLogin",
            "setWppProjectId",
            "setOauthConfig",
            "setWppTenantId",
            "setWppWorkspaceId",
            "setWppDevhubAppVersionId"
        ]),

        updateAppRootClassName() {
            const appNode = document.getElementById("appRoot");

            if (appNode) {
                appNode.className = this.wrapperClassName;
            }
        },

        async amplifyAuthorize() {
            const { isAuthorized, oauthConfig } = await getIsAuthorized();

            this.setOauthConfig(oauthConfig);
            this.setIsAuthorized(isAuthorized);

            if (isAuthorized) {
                return;
            }

            if (this.getQueryParam("iss")) {
                await this.$auth.providerLogin("Okta-OIDC-IC-LandingPage");
            }
        },

        hideAdblockModal() {
            this.localAdblockModalIsVisible = false;
        },

        hideMediaLibraryModal() {
            this.$store.dispatch(MediaLibraryAction.Close);
        },

        rememberAdblockSetting(val: boolean) {
            if (val) {
                localStorage.setItem(AdblockStorageKey, "true");
            } else {
                localStorage.removeItem(AdblockStorageKey);
            }
        },

        getQueryParam(param: string): string | LocationQueryValue[] | null {
            return this.$route.query[param] || new URLSearchParams(window.location.search).get(param);
        },

        initializeGA4() {
            const isInIframe = window.self !== window.top;
            const parentUrl = isInIframe ? document.referrer : "";
            const isOpenOS = parentUrl.includes("wpp.com") || parentUrl.includes("os.wpp.com");
            const isWppContext = this.getQueryParam("type") === "wpp" || isOpenOS;

            if (!isInIframe || !isOpenOS) {
                initGTM({ dataLayerName: "wppopen_dataLayer" });
            }

            this.ga4Tracker = new GA4Tracker("wppopen_dataLayer", isInIframe && isOpenOS);

            this.ga4Tracker.setContextData({
                contextType: isWppContext ? "os" : "standalone",
                tenantName: isWppContext ? "WPP Open" : undefined
            });
        },

        handleWppContextReceived(context: OpenOSContext) {
            this.openOSContext = context;
            this.wppContextReceived = true;

            if (this.ga4Tracker) {
                this.initializeGA4WithOpenOSContext(context);
            }
        },

        setLocalWppProjectId(projectId: string) {
            this.wppProjectId = projectId;
            this.setWppProjectId(projectId);

            if (this.ga4Initialized && this.ga4Tracker && this.openOSContext) {
                this.updateGA4WithProjectData();
            }
        },
        setLocalWppTenantId(tenantId: string) {
            this.setWppTenantId(tenantId);
        },
        setLocalWppWorkspaceId(workspaceId: string) {
            this.setWppWorkspaceId(workspaceId);
        },
        setLocalWppDevhubAppVersionId(devhubAppVersionId: string) {
            this.setWppDevhubAppVersionId(devhubAppVersionId);
        },

        updateGA4WithProjectData() {
            if (!this.ga4Tracker || !this.openOSContext) return;

            const updatedContext = {
                ...this.openOSContext,
                project: {
                    ...this.openOSContext.project,
                    id: this.wppProjectId,
                    name: this.openOSContext.project?.name || `Project ${this.wppProjectId}`
                }
            };

            this.ga4Tracker.setContextData({
                contextType: "os",
                project: updatedContext.project?.name,
                tenantName: updatedContext.tenant?.name || "WPP Open"
            });
        },

        async initializeGA4WithOpenOSContext(context: OpenOSContext) {
            if (!this.ga4Tracker || !context) {
                return;
            }

            try {
                if (context.userDetails) {
                    await this.ga4Tracker.setUserData({
                        email: context.userDetails.email,
                        company: context.userDetails.company || "unknown",
                        countryCode: context.userDetails.countryAlpha2Code || "unknown",
                        department: context.userDetails.department || "unknown",
                        jobRole: context.userDetails.jobTitle || "unknown",
                        region: context.userDetails.geoRegion || "unknown",
                        isExternal: context.userDetails.isExternal || false
                    });
                } else {
                    console.warn("No userDetails found in context - MANDATORY DATA MISSING");
                }

                const workspaceData: {
                    client?: string;
                    market?: string;
                    brand?: string;
                    region?: string;
                    industry?: string;
                    custom?: string;
                } = {};

                if (context.project?.contextHierarchy && Array.isArray(context.project.contextHierarchy)) {
                    context.project.contextHierarchy.forEach(item => {
                        switch (item.title.toLowerCase()) {
                            case "client":
                                workspaceData.client = item.value || undefined;
                                break;
                            case "market":
                                workspaceData.market = item.value || undefined;
                                break;
                            case "brand":
                                workspaceData.brand = item.value || undefined;
                                break;
                            case "region":
                                workspaceData.region = item.value || undefined;
                                break;
                            case "industry":
                                workspaceData.industry = item.value || undefined;
                                break;
                            default:
                                if (item.title && item.title !== "test") {
                                    workspaceData.custom = item.value || undefined;
                                }
                                break;
                        }
                    });
                }

                this.ga4Tracker.setWorkspaceData({
                    market: workspaceData.market || undefined,
                    industry: workspaceData.industry || undefined,
                    brand: workspaceData.brand || undefined,
                    client: workspaceData.client || undefined,
                    region: workspaceData.region || undefined,
                    custom: workspaceData.custom || undefined
                });

                if (context.appInstance) {
                    this.ga4Tracker.setApplicationData({
                        applicationId: context.appInstance.devhubAppId,
                        applicationInstanceId: context.appInstance.id,
                        productType: context.appInstance.devhubMetadata?.productType || "unknown",
                        nocodeApptype: context.appInstance.devhubMetadata?.noCodeAppType || undefined,
                        productName: "Assemble",
                        productCategory: "Production",
                        productSubcategory: "Content"
                    });
                } else {
                    console.warn("No appInstance found in context - APPLICATION DATA MISSING");
                }

                const projectName =
                    context.project?.name || (this.wppProjectId ? `Project ${this.wppProjectId}` : undefined);

                this.ga4Tracker.setContextData({
                    contextType: "os",
                    project: projectName || undefined,
                    tenantName: context.tenant?.name || "WPP Open"
                });

                this.ga4Initialized = true;

                this.ga4Tracker.trackPageView();
                this.ga4Tracker.trackAppLaunch();
            } catch (error) {
                console.error("Failed to initialize GA4 with Open OS context:", error);
                console.error("Context structure:", context);
            }
        },

        async initializeGA4WithStandaloneUser() {
            if (!this.ga4Tracker || !this.me || this.isLocalhost) return;

            try {
                await this.ga4Tracker.setUserData({
                    email: this.me.id,
                    company: this.me.company || "unknown",
                    countryCode: this.me.countryCode || "unknown",
                    department: this.me.department || "unknown",
                    jobRole: this.me.title || this.me.jobRole || "unknown",
                    region: this.me.region || "unknown",
                    isExternal: this.me.isExternal || false
                });

                this.ga4Tracker.setApplicationData({
                    applicationId: "intelligent-content-production",
                    applicationInstanceId: "intelligent-content-production",
                    productType: "STANDALONE_APPLICATION",
                    productName: "Assemble",
                    productCategory: "Production",
                    productSubcategory: "Content"
                });

                this.ga4Initialized = true;

                this.ga4Tracker.trackPageView();
                this.ga4Tracker.trackUserLogin();
                this.ga4Tracker.trackAppLaunch();
            } catch (error) {
                console.error("Failed to initialize GA4 with standalone user", error);
            }
        },

        trackInitialPageView() {
            if (this.ga4Initialized && this.ga4Tracker) {
                this.ga4Tracker.trackPageView();
            }
        }
    },

    apollo: {
        me: {
            query: meQuery,
            pollInterval: 30000,
            skip() {
                return !this.isAuthorized;
            },
            result({ data, error }) {
                if (!error && data.me) {
                    this.$store.commit(AuthMutation.SetMe, { me: data.me });
                    if (!this.isWppLogin && !this.ga4Initialized) {
                        this.initializeGA4WithStandaloneUser();
                    }
                }
            }
        },
        roles: {
            query: rolesQuery,
            skip() {
                return (
                    !this.isAuthorized ||
                    !this.$store.state.auth.me ||
                    !this.$auth.userCan(this.$auth.Actions.CanListRoles)
                );
            },
            result({ data, error }) {
                if (!error) {
                    this.$store.dispatch(RolesAction.SetRoles, data.roles);
                }
            }
        },
        contentGenerationApiInfo: {
            query: getContentGenerationApiInfoQuery,
            skip() {
                return !this.isAuthorized;
            },
            result({ data }) {
                this.$store.commit(AuthMutation.SetContentGenerationApiInfo, {
                    contentGenerationApiInfo: data.contentGenerationApiInfo
                });
            }
        }
    }
};
</script>

<style lang="scss">
@use "@style/variables" as *;
/////////////////////
// iview overrides
////////////////////
@use "@style/iview-overrides" as *;
/////////////////////
// main hoxton styles
////////////////////
@use "@style/hoxton" as *;

@use "@style/theme.overwrite" as *;
@use "@style/dark.theme";
</style>
